import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchEmsignerUrl } from "../../redux/action/buyerAction";
import useTranslationHook from "../hooks/useTranslationHook";
import { updateBuyerRegistrationField } from "../../redux/slice/buyerSlice";

const EmsignerIframe = ({
    propertyId,
    agreementType,
    onProceed,
    proceedLabel,
    registrationId,
    sessionId,
    currentPath,
    lotId,
    signature,
    registrationType,
    stateData,
    seller = false,
    isBidLimitIncrease=false
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslationHook();

    const [signingUrl, setSigningUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [openedInNewTab, setOpenedInNewTab] = useState(false);
    const popupRef = useRef(null);
    const agreementSigned = useSelector(
        (state) => state.buyer.buyerRegistration?.agreementSigned
    );

    const id = agreementType === "live_auction" ? sessionId : propertyId;

    // Keep a ref so stateData changes don't re-trigger loadSigningUrl (prevents double API call)
    const stateDataRef = useRef(stateData);
    stateDataRef.current = stateData;

    // Guard against StrictMode double-mount and stateData-triggered re-runs
    const loadStartedRef = useRef(false);

    const loadSigningUrl = useCallback(async () => {
        if (!id || !agreementType) return;
        if (loadStartedRef.current) return;
        loadStartedRef.current = true;
        setLoading(true);
        setError(null);

        const redirectBase = new URL(
            `${window.location.origin}${window.location.pathname}`
        );
        // stateData (e.g. stepOneData) can be large — keep it OUT of the redirect URL.
        // emsigner appends ?workflowid=... after our redirect URL; if the URL is too
        // long the workflowid gets truncated and the callback fails.
        // Store stateData in sessionStorage and put only a short key in esState.
        const currentStateData = stateDataRef.current;
        const sdKey =
            currentStateData && Object.keys(currentStateData).length > 0
                ? `emsigner_sd_${agreementType}_${id}`
                : null;
        if (sdKey) {
            sessionStorage.setItem(sdKey, JSON.stringify(currentStateData));
            console.info(
                "[EmsignerIframe] stateData saved to sessionStorage — key:",
                sdKey
            );
        }

        const statePayload = {
            at: agreementType,
            ...(propertyId && { pid: String(propertyId) }),
            ...(sessionId && { sid: String(sessionId) }),
            ...(registrationId && { rid: String(registrationId) }),
            ...(currentPath && { cp: currentPath }),
            ...(lotId && { lid: String(lotId) }),
            ...(signature && { sig: signature }),
            ...(registrationType && { rt: registrationType }),
            ...(sdKey && { sdk: sdKey }), // key reference only — full stateData is in sessionStorage
        };
        const needsEsState = !!(
            sessionId ||
            registrationId ||
            currentPath ||
            lotId ||
            registrationType ||
            sdKey
        );
        if (needsEsState) {
            redirectBase.searchParams.set(
                "esState",
                btoa(JSON.stringify(statePayload))
            );
        }

        console.info(
            "[EmsignerIframe] fetching signing URL — redirect URL:",
            redirectBase.toString()
        );
        const response = await dispatch(
            fetchEmsignerUrl(id, agreementType, redirectBase.toString(), seller,isBidLimitIncrease)
        );
        const url = seller
            ? response?.data?.url
            : (response?.data?.data?.Response?.URL ??
              response?.data?.data?.signed_doc);
        if (response?.data?.data?.signed_doc) {
            dispatch(
                updateBuyerRegistrationField({
                    field: "agreementSigned",
                    value: true,
                })
            );
        } else {
            agreementSigned &&
                dispatch(
                    updateBuyerRegistrationField({
                        field: "agreementSigned",
                        value: false,
                    })
                );
        }
        if (url) {
            console.info("[EmsignerIframe] signing URL received:", url);
            setSigningUrl(url);
        } else {
            loadStartedRef.current = false; // allow retry on failure
            setError(
                response?.msg ||
                    t("Failed to load signing agreement. Please try again.")
            );
        }
        setLoading(false);
    }, [
        id,
        agreementType,
        propertyId,
        registrationId,
        sessionId,
        currentPath,
        lotId,
        signature,
        registrationType,
        dispatch,
        t,
    ]); // stateData intentionally excluded — read via stateDataRef to avoid double API call

    useEffect(() => {
        loadSigningUrl();
    }, [loadSigningUrl]);

    useEffect(() => {
        setIframeLoaded(false);
    }, [signingUrl]);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === "AUCTION_SAVED") {
                console.info(
                    "[EmsignerIframe] AUCTION_SAVED received — navigating to:",
                    event.data.path
                );
                navigate(event.data.path);
                return;
            }
            if (event.data?.type !== "EMSIGNER_SIGNED") return;
            const raw = event.data.search || "";
            // emsigner appends ?workflowid=... to a URL that may already have ?esState=...
            // resulting in a double ?, e.g. ?esState=xxx?workflowid=yyy — normalize to &
            const normalized =
                raw.slice(0, 1) + raw.slice(1).replace(/\?/g, "&");
            const workflowid = new URLSearchParams(normalized).get(
                "workflowid"
            );
            if (!workflowid) {
                console.info(
                    "[EmsignerIframe] ignoring EMSIGNER_SIGNED — no workflowid in search:",
                    raw
                );
                return;
            }
            console.info(
                "[EmsignerIframe] EMSIGNER_SIGNED received — raw:",
                raw,
                "| normalized:",
                normalized
            );
            navigate(
                { search: normalized },
                { replace: true, state: location.state }
            );
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [navigate]);

    const openInNewTab = useCallback(() => {
        if (!signingUrl) return;
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.focus();
            return;
        }
        popupRef.current = window.open(signingUrl, "_blank");
        setOpenedInNewTab(true);
    }, [signingUrl]);

    if (loading) {
        return (
            <div className="emsigner-frame__center">
                <div className="spinner-border text-primary" role="status" />
                <p>{t("Loading signing page...")}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="emsigner-frame__center">
                <p className="text-danger">{error}</p>
                <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={loadSigningUrl}
                >
                    {t("Retry")}
                </button>
            </div>
        );
    }

    if (!signingUrl) return null;

    return (
        <div className="emsigner-frame">
            {openedInNewTab ? (
                <div className="emsigner-frame__center emsigner-frame__center--card">
                    <p>
                        {t(
                            "The signing page has been opened in a new tab. Complete the agreement there — this page will continue automatically once you're done."
                        )}
                    </p>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={openInNewTab}
                    >
                        {t("Reopen signing page")}
                    </button>
                </div>
            ) : (
                <>
                    <div className="emsigner-frame__viewport">
                        {!iframeLoaded && (
                            <div className="emsigner-frame__loading">
                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                />
                                <p>{t("Loading signing page...")}</p>
                            </div>
                        )}
                        <iframe
                            className="emsigner-frame__iframe"
                            src={signingUrl}
                            title={t("Signing Agreement")}
                            onLoad={() => setIframeLoaded(true)}
                        />
                    </div>
                    {!agreementSigned && (
                        <div className="emsigner-frame__help">
                            <span>
                                {t("Having trouble viewing the document?")}
                            </span>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={openInNewTab}
                            >
                                {t("Open signing page in a new tab")}
                            </button>
                        </div>
                    )}
                </>
            )}
            {onProceed && (
                <div className="button-action mt-3">
                    <button
                        type="button"
                        className="btn btn-primary btn-full"
                        onClick={onProceed}
                    >
                        {proceedLabel || t("Proceed to Payment")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default EmsignerIframe;
